import { Modal, Box, Text, Flex, Stack, Paper, Button, Badge, Anchor, SimpleGrid, ScrollArea } from "@mantine/core";
import {
  IconRocket,
  IconCheck,
  IconInfoCircle,
  IconLock,
  IconClock,
  IconHeart,
  IconWorld,
  IconMail,
  IconMapPin,
} from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";

interface ProfileGenerateModalProps {
  opened: boolean;
  onClose: () => void;
  onGenerate: () => void;
  variant?: "create" | "edit";
}

const CHECKLIST_ITEMS = [
  "Company overview",
  "Products & services",
  "Manufacturing capabilities",
  "Contact details",
];

export default function ProfileGenerateModal({
  opened,
  onClose,
  onGenerate,
  variant = "create",
}: ProfileGenerateModalProps) {
  const isEdit = variant === "edit";
  const isMobile = useMediaQuery("(max-width: 576px)");

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size={isMobile ? "100%" : "lg"}
      radius={isMobile ? 0 : "lg"}
      withCloseButton
      closeButtonProps={{ mr: 16 }}
      padding={0}
      fullScreen={isMobile}
      styles={{
        content: {
          borderRadius: isMobile ? 0 : 20,
        },
        body: {
          padding: 0,
          height: isMobile ? "100%" : "auto",
        },
        header: {
          marginBottom: 0,
          paddingBottom: 0,
        },
      }}
    >
      <ScrollArea
        type="auto"
        style={{
          height: isMobile ? "100%" : undefined,
          maxHeight: isMobile ? undefined : "min(80vh, 720px)",
        }}
      >
        <Box px={{ base: "md", sm: "xl" }} pb={{ base: "lg", sm: "xl" }} pt={0}>
        <Text
          component="h2"
          fz={22}
          fw={700}
          c="primary.8"
          ta="center"
          lh={1.35}
          mb="sm"
          styles={{
            root: {
              margin: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              flexWrap: "wrap",
            },
          }}
        >
          <Box
            component="span"
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: "var(--mantine-color-primary-0)",
              border: "1px solid var(--mantine-color-primary-2)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconRocket size={22} color="var(--mantine-color-primary-6)" />
          </Box>
          {isEdit ? "Update your company profile in seconds" : "Build your company profile in seconds"}
        </Text>

        <Box mb="xl" maw={480} mx="auto" w="100%">
          <Text ta="center" c="primary.7" fz="sm" mb="sm" fw={500}>
            Let GTsetu AI generate everything for you:
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" verticalSpacing="sm">
            {CHECKLIST_ITEMS.map((item) => (
              <Flex key={item} align="center" gap={8} wrap="nowrap">
                <IconCheck size={18} color="var(--mantine-color-green-7)" stroke={2.5} style={{ flexShrink: 0 }} />
                <Text fz="sm" c="slate.7" lh={1.35} style={{ flex: 1, minWidth: 0 }}>
                  {item}
                </Text>
              </Flex>
            ))}
          </SimpleGrid>
        </Box>

        <Box maw={isMobile ? undefined : 520} mx="auto" w="100%">
          <Flex justify="center">
            <Button
              fullWidth={isMobile}
              w={isMobile ? undefined : 340}
              size="md"
              radius="md"
              color="primary"
              variant="filled"
              onClick={onGenerate}
              vars={() => ({
                root: {
                  "--button-bg": "var(--mantine-color-primary-6)",
                  /* Mantine applies hover via this variable; plain styles &:hover loses to component CSS */
                  "--button-hover": "color-mix(in srgb, var(--mantine-color-primary-6) 88%, white)",
                },
              })}
              styles={{
                root: {
                  minHeight: 48,
                  paddingTop: 10,
                  paddingBottom: 10,
                  boxShadow: "0px 1px 3px rgba(20, 55, 90, 0.35), 0px 1px 2px rgba(4, 48, 92, 0.25)",
                  transition: "box-shadow 180ms ease, transform 180ms ease",
                  "&:hover:not(:disabled):not([data-disabled]):not([data-loading])": {
                    boxShadow: "0px 8px 20px rgba(4, 48, 92, 0.35), 0px 4px 8px rgba(4, 48, 92, 0.2)",
                    transform: "translateY(-2px)",
                  },
                  "&:active:not(:disabled):not([data-disabled]):not([data-loading])": {
                    transform: "translateY(0)",
                    boxShadow: "0px 2px 6px rgba(4, 48, 92, 0.3)",
                  },
                },
                label: {
                  flexDirection: "column",
                  gap: 6,
                  alignItems: "center",
                  justifyContent: "center",
                },
              }}
            >
              <Text fz="sm" fw={600} component="span" c="white" lh={1.25}>
                {isEdit ? "Update Profile with AI (Recommended)" : "Create Profile with AI (Recommended)"}
              </Text>
            </Button>
          </Flex>
        </Box>

        <Stack gap={8} align="center" mt="md" mb="sm">
          <Flex align="center" gap={8} justify="center" wrap="wrap">
            <IconInfoCircle size={16} color="var(--mantine-color-primary-6)" />
            <Text fz="sm" c="slate.6" ta="center" lh={1.4}>
             Enter your website URL → Done in minutes
            </Text>
          </Flex>
          <Flex align="center" gap={8} justify="center" wrap="wrap">
            <IconLock size={16} color="var(--mantine-color-primary-6)" />
            <Text fz="sm" c="slate.6" ta="center" lh={1.4}>
              Secure &amp; private
            </Text>
          </Flex>
          <Flex align="center" gap={8} justify="center" wrap="wrap">
            <IconClock size={16} color="var(--mantine-color-primary-6)" />
            <Text fz="sm" c="slate.6" ta="center" lh={1.4}>
              AI: ~5-10 minutes | Manual: 1–1.5 hours
            </Text>
          </Flex>
        </Stack>

        {!isEdit && (
          <>
            <Paper
              p="md"
              radius="md"
              bg="primary.0"
              style={{ border: "1px solid var(--mantine-color-primary-2)" }}
            >
              <Flex justify="space-between" align="flex-start" gap="md" mb="sm" wrap="nowrap">
                <Flex align="center" gap={10} style={{ minWidth: 0 }}>
                  <Box
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      backgroundColor: "var(--mantine-color-primary-6)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    ABC
                  </Box>
                  <Text fz="md" fw={700} c="primary.8" truncate>
                    ABC Company Pvt Ltd
                  </Text>
                </Flex>
                <Badge
                  size="xs"
                  variant="light"
                  color="primary.6"
                  style={{ flexShrink: 0, color: "var(--mantine-color-primary-6)" }}
                >
                  Sample
                </Badge>
              </Flex>

              <Text fz="xs" c="primary.7" lh={1.55} mb="md">
                ABC Company Pvt Ltd is a leading provider of innovative automation solutions and robotic technologies for various
                industries.
              </Text>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
                <Box>
                  <Text fz="xs" fw={600} c="primary.8" mb={6}>
                    Products &amp; Services
                  </Text>
                  <Stack gap={4}>
                    <Text fz="xs" c="slate.6" component="div">
                      • Industrial robots
                    </Text>
                    <Text fz="xs" c="slate.6" component="div">
                      • Automation systems
                    </Text>
                  </Stack>
                </Box>
                <Box>
                  <Text fz="xs" fw={600} c="primary.8" mb={6}>
                    Manufacturing Capabilities
                  </Text>
                  <Stack gap={4}>
                    <Text fz="xs" c="slate.6" component="div">
                      • Custom automation solutions
                    </Text>
                    <Text fz="xs" c="slate.6" component="div">
                      • Advanced robotics R&amp;D
                    </Text>
                  </Stack>
                </Box>
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <Flex align="center" gap={8}>
                  <IconWorld size={14} color="var(--mantine-color-primary-6)" style={{ flexShrink: 0 }} />
                  <Text fz="xs" c="slate.6" truncate>
                    www.abccompany.com
                  </Text>
                </Flex>
                <Flex align="center" gap={8}>
                  <IconMail size={14} color="var(--mantine-color-primary-6)" style={{ flexShrink: 0 }} />
                  <Text fz="xs" c="slate.6" truncate>
                    info@abccompany.com
                  </Text>
                </Flex>
                <Flex align="center" gap={8}>
                  <IconMapPin size={14} color="var(--mantine-color-primary-6)" style={{ flexShrink: 0 }} />
                  <Text fz="xs" c="slate.6" truncate>
                    San Francisco, CA
                  </Text>
                </Flex>
                <Flex align="center" gap={8}>
                  <IconMapPin size={14} color="var(--mantine-color-primary-6)" style={{ flexShrink: 0 }} />
                  <Text fz="xs" c="slate.6" truncate>
                    San Francisco, CA
                  </Text>
                </Flex>
              </SimpleGrid>
            </Paper>
          </>
        )}

        <Flex justify="center" mt="xl">
          <Anchor
            component="button"
            type="button"
            fz="sm"
            fw={500}
            c="primary.6"
            underline="never"
            onClick={onClose}
            styles={{ root: { "&:hover": { color: "var(--mantine-color-primary-7)" } } }}
          >
            {isEdit ? "Continue editing manually →" : "Skip & create manually →"}
          </Anchor>
        </Flex>
        </Box>
      </ScrollArea>
    </Modal>
  );
}
